/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/VirtualLinePauseAppointmentModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import styles from "./index.module.css";

/**
 * Parses and validates pause duration per API: optional `-`, then `H:mm` / `HH:mm`; `24:00` only for 24h.
 * @param {string} raw
 * @returns {string | null} Normalized string for the API, or null if invalid.
 */
export const normalizeVirtualLinePauseDuration = (raw) => {
  const s = String(raw ?? "").trim();
  if (!s) {
    return null;
  }
  const neg = s.startsWith("-");
  const rest = neg ? s.slice(1).trim() : s;
  const m = /^(\d{1,2}):(\d{2})$/.exec(rest);
  if (!m) {
    return null;
  }
  const h = Number(m[1], 10);
  const minutes = Number(m[2], 10);
  if (!Number.isFinite(h) || !Number.isFinite(minutes)) {
    return null;
  }
  if (minutes < 0 || minutes > 59) {
    return null;
  }
  if (h === 24) {
    if (minutes !== 0) {
      return null;
    }
  } else if (h < 0 || h > 23) {
    return null;
  }
  const sign = neg ? "-" : "";
  return `${sign}${h}:${String(minutes).padStart(2, "0")}`;
};

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {Record<string, unknown> | null} [props.appointment]
 * @param {(pauseDuration: string) => Promise<{ error?: string }>} [props.onConfirmPause]
 * @param {boolean} [props.isSubmitting]
 */
const VirtualLinePauseAppointmentModal = ({
  open,
  onClose,
  appointment,
  onConfirmPause,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  const dialogRef = useRef(null);
  const [pauseDuration, setPauseDuration] = useState("0:00");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    const handleClose = () => {
      onClose();
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    if (open) {
      setPauseDuration("0:00");
      setFormError("");
      dialog?.showModal();
    } else {
      dialog?.close();
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
  }, [open, onClose]);

  const handleDurationChange = (e) => {
    setPauseDuration(e.target.value);
    if (formError) {
      setFormError("");
    }
  };

  const handleCancel = () => {
    dialogRef.current?.close();
  };

  const handlePauseSubmit = async () => {
    if (!onConfirmPause || isSubmitting) {
      return;
    }
    if (!appointment?._id) {
      setFormError(t("pages.b2bEventUsersList.pauseModalMissingContext"));
      return;
    }
    const normalized = normalizeVirtualLinePauseDuration(pauseDuration);
    if (normalized == null) {
      setFormError(t("pages.b2bEventUsersList.pauseModalInvalidDuration"));
      return;
    }
    setFormError("");
    const result = await onConfirmPause(normalized);
    if (result?.error) {
      setFormError(result.error);
      return;
    }
    dialogRef.current?.close();
  };

  const disableActions = isSubmitting;

  return (
    <>
      {open && <div className={styles.overlay} aria-hidden />}
      <dialog
        ref={dialogRef}
        className={styles.modal}
        data-appointment-id={
          appointment?._id != null ? String(appointment._id) : undefined
        }
      >
        <div className={styles.closeButtonContainer}>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleCancel}
            disabled={disableActions}
            aria-label={t("common.close")}
          >
            &times;
          </button>
        </div>

        <div className={styles.modalInner}>
          <h2 className={styles.modalTitle}>
            {t("pages.b2bEventUsersList.pauseModalTitle")}
          </h2>

          <div className={styles.textBlock}>
            <p className={styles.leadText}>
              {t("pages.b2bEventUsersList.pauseModalQuestion")}
            </p>
            <p className={styles.promptText}>
              {t("pages.b2bEventUsersList.pauseModalDurationPrompt")}
            </p>
          </div>

          <div className={styles.fieldBlock}>
            <label
              className={styles.fieldLabel}
              htmlFor="virtual-line-pause-duration"
            >
              {t("pages.b2bEventUsersList.pauseModalDurationLabel")}
            </label>
            <div className={styles.durationInputContainer}>
              <input
                id="virtual-line-pause-duration"
                type="text"
                name="pauseDuration"
                value={pauseDuration}
                onChange={handleDurationChange}
                className={styles.durationInput}
                placeholder={t("common.durationPlaceholder")}
                autoComplete="off"
                disabled={disableActions}
                aria-invalid={Boolean(formError)}
              />
              <span className={styles.durationUnit}>
                {t("pages.b2bEventForm.hoursShort")}
              </span>
            </div>
          </div>

          {formError ? (
            <p className={styles.formError} role="alert">
              {formError}
            </p>
          ) : null}
        </div>

        <div className={styles.modalButtons}>
          <button
            type="button"
            onClick={handleCancel}
            className={styles.cancelButton}
            disabled={disableActions}
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className={styles.confirmButton}
            onClick={handlePauseSubmit}
            disabled={disableActions}
          >
            {isSubmitting
              ? t("pages.b2bEventUsersList.pauseModalSubmitting")
              : t("pages.b2bEventUsersList.pauseModalConfirm")}
          </button>
        </div>
      </dialog>
    </>
  );
};

export default VirtualLinePauseAppointmentModal;
